import type { NavEntry, NavHistoryStorage, NavNodeType, ParentInfo } from './types';

/**
 * Validate that an entry has required fields
 */
function isValidEntry(entry: unknown): entry is NavEntry {
  if (!entry || typeof entry !== 'object') return false;
  const e = entry as Partial<NavEntry>;
  return (
    typeof e.path === 'string' &&
    typeof e.name === 'string' &&
    typeof e.lastVisited === 'number' &&
    typeof e.type === 'string' &&
    (!e.parents || Array.isArray(e.parents))
  );
}

/**
 * Load navigation entries from localStorage
 */
export function loadEntries(storageKey: string): NavEntry[] {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return [];

    const parsed = JSON.parse(stored) as Partial<NavHistoryStorage>;

    // Validate basic structure
    if (!Array.isArray(parsed.entries)) {
      return [];
    }

    // Filter out any malformed entries
    return parsed.entries.filter(isValidEntry);
  } catch {
    // Failed to parse or load - return empty array
    return [];
  }
}

/**
 * Save navigation entries to localStorage
 */
export function saveEntries(storageKey: string, entries: NavEntry[]): boolean {
  try {
    const storage: NavHistoryStorage = {
      lastUpdated: Date.now(),
      entries,
    };

    localStorage.setItem(storageKey, JSON.stringify(storage));

    // Dispatch custom event for same-tab updates (storage event doesn't fire in same tab)
    if (globalThis.window !== undefined) {
      globalThis.dispatchEvent(
        new CustomEvent('navHistoryUpdate', {
          detail: { storageKey },
        }),
      );
    }

    return true;
  } catch {
    // localStorage might be full or unavailable - fail silently
    return false;
  }
}

/**
 * Upsert (insert or update) a navigation entry in an array
 *
 * **Note:** This is a pure array manipulation function that does NOT interact with localStorage.
 * To persist changes, call `saveEntries()` separately with the returned array.
 *
 * @param entries - The current array of navigation entries
 * @param path - The path of the entry to upsert
 * @param name - The display name of the entry
 * @param type - The type of navigation node
 * @param parents - Optional parent breadcrumb information
 * @returns A new array with the entry added or updated (preserves pinned state if updating)
 *
 * @example
 * ```ts
 * // Update the array (in-memory only)
 * let updated = upsertEntry(entries, '/path', 'Name', 'api', parents);
 *
 * // Then persist to localStorage separately
 * saveEntries(storageKey, updated);
 * ```
 */
export function upsertEntry(
  entries: NavEntry[],
  path: string,
  name: string,
  type: NavNodeType,
  parents?: ParentInfo[],
): NavEntry[] {
  // Validate path format - silently ignore invalid paths
  if (!path || !path.startsWith('/')) {
    return entries;
  }

  const existingIndex = entries.findIndex((e) => e.path === path);
  const existingEntry = entries.find((e) => e.path === path);

  const newEntry: NavEntry = {
    path,
    name,
    type,
    lastVisited: Date.now(),
    parents,
    // Preserve pinned state from existing entry
    pinned: existingEntry?.pinned ?? false,
  };

  const updated = [...entries];

  if (existingIndex !== -1) {
    // Remove existing entry
    updated.splice(existingIndex, 1);
  }

  // Keep index if entry was pinned, otherwise insert before first unpinned entry
  const index = existingEntry?.pinned ? existingIndex : updated.findIndex((e) => !e.pinned);
  if (index === -1) {
    updated.push(newEntry);
  } else {
    updated.splice(index, 0, newEntry);
  }
  return updated;
}

/**
 * Clear navigation history from localStorage
 */
export function clearEntries(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);

    // Notify listeners
    if (globalThis.window !== undefined) {
      globalThis.dispatchEvent(
        new CustomEvent('navHistoryUpdate', {
          detail: { storageKey },
        }),
      );
    }
  } catch {
    // Silently fail if localStorage is not available
  }
}
